<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Education History"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.school"
            autocomplete="off"
            label="School Name"
            outlined
            :error-messages="getErrorMessages($v.section.school)"
            @input="$v.section.school.$touch()"
            @blur="$v.section.school.$touch()"
          />
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.citystate"
            autocomplete="off"
            label="School City/State"
            outlined
            :error-messages="getErrorMessages($v.section.citystate)"
            @input="$v.section.citystate.$touch()"
            @blur="$v.section.citystate.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-autocomplete
            v-model="section.country_id"
            :items="optionsCountries"
            item-text="name"
            item-value="id"
            label="School Country"
            outlined
            :error-messages="getErrorMessages($v.section.country_id)"
            @input="$v.section.country_id.$touch()"
            @blur="$v.section.country_id.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Start Date"
            :formatable-date="section.start_date"
            :format="section.start_date_format"
            :uncertain="section.start_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.start_date)"
            @update-box="onStartDateUncertainUpdate"
            @update-date="onStartDateUpdate"
            @tab-out="$v.section.start_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="End Date"
            :formatable-date="section.end_date"
            :format="section.end_date_format"
            :uncertain="section.end_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.end_date)"
            @update-box="onEndDateUncertainUpdate"
            @update-date="onEndDateUpdate"
            @tab-out="$v.section.end_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages($v.section.notes)"
            @input="$v.section.notes.$touch()"
            @blur="$v.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { helpers } from '@vuelidate/validators'
import { rules } from '@/utilities/validation-rules'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'EducationHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        citystate: {},
        country_id: {},
        end_date: {
          after_start_date: helpers.withMessage('End date must be after start date',
            _ => rules.startBeforeEndDate(this.section.start_date, this.section.end_date) === true),
        },

        end_date_uncertain: {},

        notes: {},
        school: {},
        start_date: {
          before_end_date: helpers.withMessage('Start date must be before end date',
            _ => rules.startBeforeEndDate(this.section.start_date, this.section.end_date) === true),
        },

        start_date_uncertain: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `education-history-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peEducationHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeEducationHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsCountries () { return this.getCachedDropdown('countries') },
    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peEducationHistoriesCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeEducationHistoriesCard(this.sectionIdentifier)

    this.getDropdownOptions({ options: false, path: 'countries' })
  },

  methods: {

    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peEducationHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peEducationHistoriesCardCreateUndo()
      else {
        return this.peEducationHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peEducationHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peEducationHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    // on-end_date update
    onEndDateUncertainUpdate (value) {
      this.$v.section.end_date.$touch()
      this.$v.section.end_date_uncertain.$touch()
      this.section.end_date_uncertain = value
    },

    onEndDateUpdate (value) {
      this.$v.section.end_date.$touch()
      this.$v.section.end_date_uncertain.$touch()
      this.section.end_date = value.date
      this.section.end_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeEducationHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    onStartDateUncertainUpdate (value) {
      this.$v.section.start_date.$touch()
      this.$v.section.start_date_uncertain.$touch()
      this.section.start_date_uncertain = value
    },

    onStartDateUpdate (value) {
      this.$v.section.start_date.$touch()
      this.$v.section.start_date_uncertain.$touch()
      this.section.start_date = value.date
      this.section.start_date_format = value.format
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peEducationHistoriesCardCreateUndo',
      'setPeEducationHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeEducationHistoriesCard',
      'peEducationHistoriesCardCreate',
      'peEducationHistoriesCardDelete',
      'peEducationHistoriesCardRefresh',
      'peEducationHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
